import type { ReactNode } from 'react';
import { Inbox, Search, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface EmptyStateProps {
  /** Lucide icon component (rendered at h-12 w-12). Defaults to Inbox / Search depending on `isFiltered`. */
  Icon?: LucideIcon;
  /** Heading text. Defaults shift based on `isFiltered`. */
  heading?: ReactNode;
  /** One-line supporting copy. */
  description?: ReactNode;
  /** When true, defaults flip to "No matches" + a Clear filters button. */
  isFiltered?: boolean;
  /** Called by the default "Clear filters" button when `isFiltered` is true. */
  onClearFilters?: () => void;
  /** Primary CTA (overrides the default Clear filters button when set). */
  action?: EmptyStateAction;
  /** Secondary CTA. */
  secondaryAction?: EmptyStateAction;
  className?: string;
}

export function EmptyState({
  Icon,
  heading,
  description,
  isFiltered = false,
  onClearFilters,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  const DisplayIcon = Icon ?? (isFiltered ? Search : Inbox);
  const displayHeading = heading ?? (isFiltered ? 'No matches' : 'Nothing here yet');
  const displayDescription =
    description ?? (isFiltered ? 'Try adjusting your filters or search.' : 'There is nothing to display.');

  const primaryAction: EmptyStateAction | undefined =
    action ?? (isFiltered && onClearFilters ? { label: 'Clear filters', onClick: onClearFilters } : undefined);

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center py-12 px-4 text-center',
        className,
      )}
      role="status"
      aria-label={typeof displayHeading === 'string' ? displayHeading : undefined}
    >
      <DisplayIcon className="h-12 w-12 text-muted-foreground/50 mb-4" aria-hidden="true" />
      <h3 className="text-base font-medium text-foreground mb-1">{displayHeading}</h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-4">{displayDescription}</p>
      {(primaryAction || secondaryAction) && (
        <div className="flex items-center gap-2">
          {secondaryAction && (
            <Button variant="outline" size="sm" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
          {primaryAction && (
            <Button size="sm" onClick={primaryAction.onClick}>
              {primaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
